import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Editor } from '@tinymce/tinymce-react';
import { authAPI, serviceAPI } from '../utils/authUtils';
import { useNavigate } from 'react-router-dom';
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';
import { scrollToForm } from '../utils/adminFormUtils';
import { useInvalidateCache } from '../utils/useCache';
import { handleError } from '../utils/errorHandler';
import ErrorDisplay from '../components/ErrorDisplay';
import '../styles/adminCommonStyles.css';

// Suppress tinyMCE console errors and warnings
const tinyMCEErrorPatterns = [
  'Failed to load plugin',
  'tinymce',
  'cdn.tiny.cloud',
  'sp.tinymce.com',
  'maps.googleapis.com',
  'ERR_BLOCKED_BY_CLIENT'
];

const shouldSuppressMessage = (message) => {
  const msg = message?.toString?.() || String(message);
  return tinyMCEErrorPatterns.some(pattern => msg.includes(pattern));
};

const originalError = console.error;
const originalWarn = console.warn;

console.error = function(...args) {
  if (!shouldSuppressMessage(args[0])) {
    originalError.apply(console, args);
  }
};

console.warn = function(...args) {
  if (!shouldSuppressMessage(args[0])) {
    originalWarn.apply(console, args);
  }
}

const TINYMCE_API_KEY = process.env.REACT_APP_TINYMCE_API_KEY;

export default function AdminServices() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const navigate = useNavigate();
  const refetch = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    try {
      const response = await serviceAPI.getServices();
      const { normalizeList } = await import('../utils/responseHelpers');
      setServices(normalizeList(response));
    } catch (err) {
      if (err.response?.status === 401) {
        setFetchError('Authentication failed. Please log in again.');
      } else {
        const errorMsg = err.response?.data?.message || err.message || 'Failed to fetch services';
        setFetchError(typeof errorMsg === 'string' ? errorMsg : 'Failed to fetch services');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);
  const { clear, clearResource } = useInvalidateCache();
  const [error, setError] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addForm, setAddForm] = useState({ title: '', description: '' });
  const [addError, setAddError] = useState(null);
  const [addLoading, setAddLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editForm, setEditForm] = useState({ id: '', title: '', description: '' });
  const [editError, setEditError] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const addModalRef = useRef(null);
  const editModalRef = useRef(null);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const checkUserRole = () => {
    try {
      const hasSuperAdminRole = authAPI.hasRole('superadmin');
      setIsSuperAdmin(hasSuperAdminRole);
    } catch (err) {
      setIsSuperAdmin(false);
    }
  };

  useEffect(() => {
    checkUserRole();
    refetch();
  }, [refetch]);

  // Scroll to top when modals open - only if form exceeds viewport height
  useEffect(() => {
    if (showAddModal && addModalRef.current) {
      setTimeout(() => {
        const modalContent = addModalRef.current;
        if (modalContent && modalContent.scrollHeight > window.innerHeight) {
          scrollToForm();
        }
      }, 0);
    }
  }, [showAddModal]);

  useEffect(() => {
    if (showEditModal && editModalRef.current) {
      setTimeout(() => {
        const modalContent = editModalRef.current;
        if (modalContent && modalContent.scrollHeight > window.innerHeight) {
          scrollToForm();
        }
      }, 0);
    }
  }, [showEditModal]);

  const handleAddInput = (e) => {
    setAddForm({ ...addForm, [e.target.name]: e.target.value });
  };

  const handleAddSubmit = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddError(null);
    try {
        const resp = await serviceAPI.createService(addForm);
      setShowAddModal(false);
      setAddForm({ title: '', description: '' });
      showSuccess('Service added successfully!');
        // Invalidate services cache and refetch
        try { const newId = resp?.data?.id || resp?.data?.service?.id; if (newId) clearResource('services', newId); else clear('/api/services'); } catch(e){console.warn('Cache clear failed',e)}
        await refetch();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to add service';
      setAddError(typeof errorMsg === 'string' ? errorMsg : 'Failed to add service');
    } finally {
      setAddLoading(false);
    }
  };

  const handleEditClick = (service) => {
    setEditForm({ id: service.id, title: service.title ?? service.name, description: service.description });
    setShowEditModal(true);
    setEditError(null);
    // Scroll to edit form
    setTimeout(() => {
      scrollToForm();
    }, 0);
  };

  const handleEditInput = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);
    setEditError(null);
    try {
      const { id, ...updateData } = editForm;
      const resp = await serviceAPI.updateService(id, updateData);
      setShowEditModal(false);
      showSuccess('Service updated successfully!');
      try { clearResource('services', id); } catch(e){console.warn('Cache clear failed',e)}
      await refetch();
    } catch (err) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to update service';
      setEditError(typeof errorMsg === 'string' ? errorMsg : 'Failed to update service');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteService = async (serviceId, serviceName) => {
    if (!isSuperAdmin) {
      alert('Access denied. Only superadmin can delete services.');
      return;
    }
    const confirmDelete = window.confirm(`Are you sure you want to delete "${serviceName}"? This action cannot be undone.`);
    if (!confirmDelete) return;
    setDeleteLoading(serviceId);
    setError(null);
    try {
      await serviceAPI.deleteService(serviceId);
      showSuccess('Service deleted successfully!');
      try { clear('/api/services'); } catch(e){console.warn('Cache clear failed',e)}
      await refetch();
    } catch (error) {
      if (error.response?.status === 403) {
        setError('Access denied. Superadmin role required to delete services.');
      } else if (error.response?.status === 404) {
        setError('Service not found.');
      } else if (error.response?.status === 401) {
        setError('Please log in again.');
        authAPI.logout();
        navigate('/admin/login');
      } else {
        setError(error.message || 'Failed to delete service');
      }
    } finally {
      setDeleteLoading(null);
    }
  };

  const handleLogout = () => {
    authAPI.logout();
    navigate('/admin/login');
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-1 md:px-2 py-2 landscape:px-4 landscape:py-4 flex flex-col items-center justify-center bg-transparent">
      <div className="admin-page-header-container">
        <h2 className="admin-page-title">Services Management</h2>
        <div className="flex items-center gap-2 sm:gap-4 justify-center sm:justify-end">
          <span className={`${isSuperAdmin ? 'admin-badge-superadmin' : 'admin-badge-admin'}`}>
            {isSuperAdmin ? 'Super Admin' : 'Admin'}
          </span>
          <button
            onClick={handleLogout}
            className="admin-btn-danger"
          >
            Logout
          </button>
        </div>
      </div>

      {successMessage && (
        <div className="admin-alert-success">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="admin-alert-error">
          <strong>Error:</strong> <ErrorDisplay error={error} className="text-red-700 font-medium" />
        </div>
      )}

      <button
        className="admin-btn-primary-lg mb-6"
        onClick={() => {
          setShowAddModal(true);
          setTimeout(() => {
            scrollToForm();
          }, 0);
        }}
      >
        + Add New Service
      </button>

      {/* Add Modal */}
      {showAddModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content admin-modal-form" id="add-service-form" ref={addModalRef}>
            <h3 className="admin-modal-title">Add New Service</h3>
            <form onSubmit={handleAddSubmit}>
              <div className="mb-4">
                <label className="admin-label">Title</label>
                <input
                  type="text"
                  name="title"
                  value={addForm.title}
                  onChange={handleAddInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Description</label>
                <Editor
                  value={addForm.description}
                  onEditorChange={val => setAddForm(f => ({ ...f, description: val }))}
                  init={{
                    apiKey: TINYMCE_API_KEY,
                    height: 420,
                    menubar: false,
                    branding: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              {addError && (
                <div className="admin-alert-error-inline">
                  {addError}
                </div>
              )}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowAddModal(false);
                    setAddError(null);
                  }}
                  disabled={addLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-primary"
                  disabled={addLoading}
                >
                  {addLoading ? 'Adding...' : 'Add Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-content admin-modal-form" id="edit-service-form" ref={editModalRef}>
            <h3 className="admin-modal-title">Edit Service</h3>
            <form onSubmit={handleEditSubmit}>
              <div className="mb-4">
                <label className="admin-label">Title</label>
                <input
                  type="text"
                  name="title"
                  value={editForm.title}
                  onChange={handleEditInput}
                  className="admin-input"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="admin-label">Description</label>
                <Editor
                  value={editForm.description}
                  onEditorChange={val => setEditForm(f => ({ ...f, description: val }))}
                  init={{
                    apiKey: TINYMCE_API_KEY,
                    height: 420,
                    menubar: false,
                    branding: false,
                    analytics: false,
                    external_plugins: {},
                    plugins: 'advlist autolink lists link image charmap anchor searchreplace visualblocks code fullscreen insertdatetime media table help wordcount',
                    toolbar: 'undo redo | formatselect | bold italic backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | removeformat | help'
                  }}
                />
              </div>
              {editError && (
                <div className="admin-alert-error-inline">
                  {editError}
                </div>
              )}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  className="admin-btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setEditError(null);
                  }}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="admin-btn-success"
                  disabled={editLoading}
                >
                  {editLoading ? 'Updating...' : 'Update Service'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Services Table */}
      {loading && (
        <LoadingSpinner message="Loading services..." bgColor="bg-white" spinnerColor="border-[#73ab40]" py={12} />
      )}

      {!loading && (
        <div className="admin-table-container">
          <table className="admin-table">
            <thead className="admin-table-head">
              <tr>
                <th className="admin-table-header-cell">Title</th>
                <th className="admin-table-header-cell">Description</th>
                <th className="admin-table-header-cell">Actions</th>
              </tr>
            </thead>
            <tbody className="admin-table-body-row">
              {services.length === 0 ? (
                <tr>
                  <td colSpan="3" className="py-8 px-4 text-center text-gray-500">
                    <div className="text-lg">No services found</div>
                    <div className="text-sm mt-1">Add your first service to get started!</div>
                  </td>
                </tr>
              ) : (
                services.map((service) => (
                  <tr key={service.id} className="hover:bg-blue-50 transition-colors">
                    <td className="admin-table-cell font-medium">
                      {service.title && service.title.length > 50 ? service.title.slice(0, 50) + '…' : service.title}
                    </td>
                    <td className="admin-table-cell-truncate">
                      <SafeRichText content={service.description && service.description.length > 50 ? service.description.slice(0, 50) + '…' : service.description} />
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex flex-col gap-1 items-stretch justify-center">
                        <button
                          onClick={() => handleEditClick(service)}
                          className="admin-btn-table-edit"
                        >
                          Edit
                        </button>
                        {isSuperAdmin ? (
                          <button
                            onClick={() => handleDeleteService(service.id, service.title)}
                            disabled={deleteLoading === service.id}
                            className="admin-btn-table-danger"
                          >
                            {deleteLoading === service.id ? 'Deleting...' : 'Delete'}
                          </button>
                        ) : (
                          <button
                            disabled
                            className="px-2 py-1 bg-gray-400 text-white rounded-lg cursor-not-allowed text-xs"
                            title="Superadmin role required to delete services"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}